import { motion } from "framer-motion";
import {
  Users,
  CalendarDays,
  BarChart3,
  UserCog,
  FileText,
  Heart,
  Bell,
  Smartphone,
  TrendingUp,
} from "lucide-react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const sparkData = [
  { v: 32 }, { v: 45 }, { v: 38 }, { v: 52 }, { v: 48 },
  { v: 61 }, { v: 55 }, { v: 72 }, { v: 68 }, { v: 80 },
];

const barData = [
  { m: "E", a: 62, b: 28 },
  { m: "F", a: 58, b: 31 },
  { m: "M", a: 71, b: 26 },
  { m: "A", a: 65, b: 29 },
  { m: "M", a: 83, b: 27 },
  { m: "J", a: 78, b: 32 },
];

const pieData = [
  { name: "Asistencia", value: 78 },
  { name: "Resto", value: 22 },
];
const PIE_COLORS = ["#C9A227", "#3D3275"];

const teamBars = [
  { name: "Alabanza", pct: 85 },
  { name: "Jóvenes", pct: 72 },
  { name: "Niños", pct: 91 },
  { name: "Logística", pct: 64 },
];

const members = [
  { initials: "MR", name: "Marta Rodríguez", status: "Nuevo" },
  { initials: "AG", name: "Ana García", status: "Nuevo" },
  { initials: "JG", name: "Juan González", status: "Activo" },
  { initials: "PS", name: "Pedro Sánchez", status: "Activo" },
  { initials: "LF", name: "Lucía Fernández", status: "Inactivo" },
  { initials: "CA", name: "Carlos Álvarez", status: "Inactivo" },
  { initials: "RM", name: "Roberto Martínez", status: "Inactivo" },
];

const timeline = [
  { label: "Visita a María L.", time: "Hoy, 10:30" },
  { label: "Llamada a Pedro R.", time: "Ayer, 16:00" },
  { label: "Seguimiento Ana G.", time: "Lun, 09:15" },
];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07 } },
};

const card = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.75, ease: [0.25, 0.1, 0.25, 1] } },
};

function CardShell({ children, className = "", testId }) {
  return (
    <motion.div
      variants={card}
      className={`group relative rounded-[20px] border border-white/[0.07] p-6 lg:p-7 transition-all duration-300 hover:shadow-[0_16px_48px_-12px_rgba(0,0,0,0.4)] hover:-translate-y-1 overflow-hidden ${className}`}
      style={{ background: "rgba(255,255,255,0.04)" }}
      data-testid={testId}
    >
      {children}
    </motion.div>
  );
}

function IconBadge({ icon: Icon }) {
  return (
    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/[0.08] text-gold transition-colors duration-300 group-hover:bg-gold group-hover:text-ink">
      <Icon className="w-5 h-5" strokeWidth={1.8} />
    </div>
  );
}

function CardTitle({ children }) {
  return (
    <h3 className="font-display text-xl lg:text-[22px] font-bold text-white tracking-tight mt-4">
      {children}
    </h3>
  );
}

function CardDesc({ children }) {
  return <p className="text-[16px] lg:text-[17px] text-ink-4 leading-relaxed mt-2">{children}</p>;
}

export default function BentoSection() {
  return (
    <section
      id="features"
      data-testid="features-section"
      className="relative py-20 md:py-32"
      style={{ background: "linear-gradient(170deg, #1A1135 0%, #211845 50%, #1A1135 100%)" }}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute top-0 left-1/4 w-[500px] h-[500px] opacity-20 rounded-full blur-[120px]"
        style={{ background: "radial-gradient(circle, #4A3A8A 0%, transparent 70%)" }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 right-1/4 w-[400px] h-[400px] opacity-15 rounded-full blur-[100px]"
        style={{ background: "radial-gradient(circle, #C9A227 0%, transparent 70%)" }}
      />

      <div className="relative z-10 mx-auto max-w-[1920px] px-4 md:px-6 xl:px-8">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.75 }}
          className="text-center max-w-3xl mx-auto mb-14 lg:mb-16"
        >
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-gold mb-4">
            Para que nada se te escape
          </p>
          <h2
            data-testid="features-title"
            className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight"
          >
            Menos tiempo en hojas de cálculo. Más tiempo con las personas.
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-ink-4 sm:text-xl">
            Cada módulo está pensado para quitarte una carga de encima, no para añadir una pantalla más.
          </p>
        </motion.div>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 auto-rows-[minmax(190px,auto)] gap-4 lg:gap-5"
        >
          {/* 1. Gestión de miembros — 2 col, 2 row */}
          <CardShell className="md:col-span-2 md:row-span-2" testId="feature-card-0">
            <IconBadge icon={Users} />
            <CardTitle>Gestión de miembros</CardTitle>
            <CardDesc>
              Conoce a cada persona: su familia, su historia y quién la está acompañando. Nadie se queda en el anonimato.
            </CardDesc>
            <div className="mt-5 space-y-2.5">
              {members.map((m) => (
                <div
                  key={m.initials}
                  className="flex items-center gap-3 rounded-xl bg-white/[0.04] border border-white/[0.06] px-4 py-2.5"
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-violet-fuerte/40 text-sm font-bold text-white">
                    {m.initials}
                  </div>
                  <span className="text-[15px] font-medium text-white/90 flex-1">{m.name}</span>
                  <span
                    className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                      m.status === "Nuevo"
                        ? "bg-gold/20 text-gold"
                        : m.status === "Inactivo"
                        ? "bg-[#F4F3F8] text-[#6B6480]"
                        : "bg-[#EDF8F3] text-[#2D8A5E]"
                    }`}
                  >
                    {m.status}
                  </span>
                </div>
              ))}
            </div>
          </CardShell>

          {/* 2. Eventos y calendario */}
          <CardShell testId="feature-card-1">
            <IconBadge icon={CalendarDays} />
            <CardTitle>Eventos y calendario</CardTitle>
            <CardDesc>
              Toda la agenda de la iglesia en un solo lugar. Cada coordinador sabe qué toca, cuándo y quién es responsable.
            </CardDesc>
            <div className="mt-4 grid grid-cols-7 gap-1 text-center">
              {["L", "M", "X", "J", "V", "S", "D"].map((d) => (
                <span key={d} className="text-[11px] font-semibold text-ink-3">{d}</span>
              ))}
              {Array.from({ length: 28 }, (_, i) => {
                const day = i + 1;
                const isHighlight = [5, 12, 19, 26].includes(day);
                const isToday = day === 15;
                return (
                  <span
                    key={day}
                    className={`text-xs rounded-md py-0.5 ${
                      isToday
                        ? "bg-gold text-ink font-bold"
                        : isHighlight
                        ? "bg-violet-fuerte/40 text-violet-claro font-semibold"
                        : "text-ink-4"
                    }`}
                  >
                    {day}
                  </span>
                );
              })}
            </div>
          </CardShell>

          {/* 3. Finanzas claras */}
          <CardShell testId="feature-card-2">
            <IconBadge icon={BarChart3} />
            <CardTitle>Finanzas claras</CardTitle>
            <CardDesc>
              Llegas a cada reunión sabiendo dónde está cada euro, sin depender de una hoja a medias ni de perseguir al tesorero.
            </CardDesc>
            <div className="mt-4 h-[100px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barData} barGap={2}>
                  <Bar dataKey="a" fill="#7A68C8" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="b" fill="#C9A227" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardShell>

          {/* 4. Equipos y ministerios — 2 col */}
          <CardShell className="md:col-span-2" testId="feature-card-3">
            <IconBadge icon={UserCog} />
            <CardTitle>Equipos y ministerios</CardTitle>
            <CardDesc>
              Cada persona en el lugar donde puede servir. Ves de un vistazo quién lidera qué y dónde hacen falta manos.
            </CardDesc>
            <div className="mt-5 space-y-3">
              {teamBars.map((t) => (
                <div key={t.name} className="flex items-center gap-3">
                  <span className="text-sm font-medium text-ink-4 w-20 shrink-0">{t.name}</span>
                  <div className="flex-1 h-2.5 rounded-full bg-white/[0.06] overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${t.pct}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.75, delay: 0.2 }}
                      className="h-full rounded-full bg-gradient-to-r from-violet-medio to-gold"
                    />
                  </div>
                  <span className="text-sm font-bold text-white w-10 text-right">{t.pct}%</span>
                </div>
              ))}
            </div>
          </CardShell>

          {/* 5. Reportes inteligentes */}
          <CardShell testId="feature-card-4">
            <IconBadge icon={FileText} />
            <CardTitle>Reportes inteligentes</CardTitle>
            <CardDesc>
              Genera informes con un clic: asistencia, crecimiento y donaciones.
            </CardDesc>
            <div className="mt-3 flex items-center justify-center">
              <div className="relative w-[100px] h-[100px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      innerRadius={32}
                      outerRadius={46}
                      dataKey="value"
                      startAngle={90}
                      endAngle={-270}
                      stroke="none"
                    >
                      {pieData.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i]} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="font-display text-lg font-bold text-white">78%</span>
                </div>
              </div>
            </div>
          </CardShell>

          {/* 6. Seguimiento pastoral */}
          <CardShell testId="feature-card-5">
            <IconBadge icon={Heart} />
            <CardTitle>Seguimiento pastoral</CardTitle>
            <CardDesc>
              Sabes a quién visitaste, a quién toca llamar y quién necesita un café esta semana. Sin que nada se te escape.
            </CardDesc>
            <div className="mt-4 space-y-2.5 pl-3 border-l-2 border-white/10">
              {timeline.map((t, i) => (
                <div key={i} className="relative">
                  <div className="absolute -left-[17px] top-1.5 h-2.5 w-2.5 rounded-full bg-gold ring-2 ring-[#1A1135]" />
                  <p className="text-sm font-medium text-white/90 pl-2">{t.label}</p>
                  <p className="text-xs text-ink-4 pl-2">{t.time}</p>
                </div>
              ))}
            </div>
          </CardShell>

          {/* 7. Notificaciones */}
          <CardShell testId="feature-card-6">
            <div className="relative inline-block">
              <IconBadge icon={Bell} />
              <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-gold text-[10px] font-bold text-ink">
                3
              </span>
            </div>
            <CardTitle>Notificaciones</CardTitle>
            <CardDesc>
              Alertas para el equipo cuando hay tareas pendientes o cambios importantes.
            </CardDesc>
            <div className="mt-4 space-y-2">
              {["Nuevo miembro registrado", "Evento mañana a las 10:00"].map((n, i) => (
                <div key={i} className="flex items-center gap-2 rounded-lg bg-white/[0.04] border border-white/[0.06] px-3 py-2">
                  <div className="h-2 w-2 rounded-full bg-gold shrink-0" />
                  <span className="text-xs font-medium text-ink-4 truncate">{n}</span>
                </div>
              ))}
            </div>
          </CardShell>

          {/* 8. Acceso móvil */}
          <CardShell testId="feature-card-7">
            <IconBadge icon={Smartphone} />
            <CardTitle>Acceso móvil</CardTitle>
            <CardDesc>
              Funciona desde cualquier dispositivo. Consultas rápidas fuera de oficina.
            </CardDesc>
            <div className="mt-4 flex items-center gap-3">
              <div className="flex-1 h-[60px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={sparkData}>
                    <defs>
                      <linearGradient id="sparkGradDark" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#C9A227" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="#C9A227" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <Area
                      type="monotone"
                      dataKey="v"
                      stroke="#C9A227"
                      strokeWidth={2}
                      fill="url(#sparkGradDark)"
                      dot={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-col items-center justify-center rounded-xl bg-white/[0.06] border border-white/[0.06] px-3 py-2">
                <TrendingUp className="w-4 h-4 text-gold mb-1" />
                <span className="font-display text-lg font-black text-white">+34%</span>
              </div>
            </div>
          </CardShell>
        </motion.div>
      </div>
    </section>
  );
}
